import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CkSelection, CkTableQuery, CkValueOption } from "@NP-Webber/crm-kit/dist/next/index.js";
import { EMPTY_TABLE_QUERY, filterOnly, queryToParams, type TableParams } from "./tableQuery";

/**
 * State of a DataTable in server mode (Turbo anatomy, DonorsNext.jsx): the table query, the named params of the
 * screen (tabs / quick cards / period), loading with a stale-response guard, all rows of the result (export,
 * "all results" bulk actions) and the column filter values.
 */
export type ServerPage<R> = { items: R[]; total: number };

/** an enum column whose server values are keys: shown by label, sent back as the key */
export type EnumColumn = { label: (value: string) => string; options?: Array<{ value: string; label: string }> };

export type ValuesResponse = { items: Array<{ value: string; count: number }> };

const FETCH_ALL_PAGE = 1000;
const FETCH_ALL_MAX_PAGES = 200;

export function useServerTable<R extends { id: string }, P extends ServerPage<R>>(opts: {
  load: (params: TableParams) => Promise<P>;
  loadValues?: (params: TableParams) => Promise<ValuesResponse>;
  /** the screen's own params (stage tab, quick card, period…) — sent with every request */
  extra?: TableParams;
  defaultQuery?: Partial<CkTableQuery>;
  enums?: Record<string, EnumColumn>;
  /** bool columns: values 1 / 0 shown as כן / לא */
  bools?: string[];
  errorText?: string;
}) {
  const [query, setQuery] = useState<CkTableQuery>(() => ({ ...EMPTY_TABLE_QUERY, ...(opts.defaultQuery ?? {}) }));
  const [data, setData] = useState<P | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const optsRef = useRef(opts);
  optsRef.current = opts;
  const extraKey = JSON.stringify(opts.extra ?? {});

  /** label picked in a values tab → server value (learned from the values the server returned) */
  const learned = useRef(new Map<string, Map<string, string>>());
  const toServer = useCallback((colKey: string, label: string) => {
    const hit = learned.current.get(colKey)?.get(label);
    if (hit !== undefined) return hit;
    return optsRef.current.enums?.[colKey]?.options?.find((o) => o.label === label)?.value ?? label;
  }, []);

  const paramsOf = useCallback((q: CkTableQuery): TableParams => ({ ...(optsRef.current.extra ?? {}), ...queryToParams(q, { toServer }) }), [toServer]);
  const lastParams = useRef<TableParams>({});

  const seq = useRef(0);
  useEffect(() => {
    const mine = ++seq.current;
    const params = paramsOf(query);
    lastParams.current = params;
    setLoading(true);
    optsRef.current
      .load(params)
      .then((res) => {
        if (mine !== seq.current) return; // a newer request is on its way
        setData(res);
        setError(null);
      })
      .catch((e: unknown) => {
        if (mine !== seq.current) return;
        setError((e as { message?: string })?.message || optsRef.current.errorText || "החיבור לשרת נכשל. הנתונים שלך שמורים.");
      })
      .finally(() => {
        if (mine === seq.current) setLoading(false);
      });
  }, [query, extraKey, reloadKey, paramsOf]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  /** back to page 1 (after the screen's own params change) */
  const firstPage = useCallback(() => setQuery((q) => (q.page === 1 ? q : { ...q, page: 1 })), []);

  /** every row of the current result (pages of 1000 with export=1) */
  const fetchAll = useCallback(async (extra: TableParams = {}): Promise<R[]> => {
    const base = { ...lastParams.current, ...extra, export: "1" };
    const out: R[] = [];
    for (let page = 1; page <= FETCH_ALL_MAX_PAGES; page += 1) {
      const res = await optsRef.current.load({ ...base, page, limit: FETCH_ALL_PAGE });
      out.push(...res.items);
      if (!res.items.length || res.items.length < FETCH_ALL_PAGE || out.length >= res.total) break;
    }
    return out;
  }, []);

  const resolveIds = useCallback(async (sel: CkSelection): Promise<string[]> => (sel.all ? (await fetchAll()).map((r) => r.id) : sel.ids), [fetchAll]);

  /** rows of a selection: the checked rows of this page, or all results */
  const resolveRows = useCallback(
    async (sel: CkSelection): Promise<R[]> => {
      if (sel.all || !sel.count) return fetchAll();
      const ids = new Set(sel.ids);
      const onPage = (data?.items ?? []).filter((r) => ids.has(r.id));
      if (onPage.length === ids.size) return onPage;
      return (await fetchAll()).filter((r) => ids.has(r.id));
    },
    [data, fetchAll],
  );

  const getValueOptions = useCallback(
    async (colKey: string, search: string): Promise<CkValueOption[]> => {
      const { loadValues, enums, bools } = optsRef.current;
      if (!loadValues) return [];
      try {
        const res = await loadValues({ ...filterOnly(lastParams.current), column: colKey, ...(search ? { search } : {}) });
        const en = enums?.[colKey];
        const isBool = bools?.includes(colKey);
        const map = new Map<string, string>();
        const out = res.items.map((it) => {
          if (isBool) return { value: it.value === "1" ? "כן" : it.value === "0" ? "לא" : it.value, count: it.count };
          if (en) {
            const label = en.label(it.value) || it.value;
            map.set(label, it.value);
            return { value: label, count: it.count };
          }
          return { value: it.value, count: it.count };
        });
        if (en) {
          const known = learned.current.get(colKey) ?? new Map<string, string>();
          map.forEach((v, k) => known.set(k, v));
          learned.current.set(colKey, known);
        }
        return out;
      } catch {
        return [];
      }
    },
    [],
  );

  const rows = useMemo(() => data?.items ?? [], [data]);
  return {
    query,
    setQuery,
    firstPage,
    data,
    rows,
    total: data?.total ?? 0,
    loading,
    error,
    reload,
    /** the params of the last request (filters + extra + paging) */
    lastParams: () => lastParams.current,
    fetchAll,
    resolveIds,
    resolveRows,
    getValueOptions,
  };
}
